import { api } from '../client';
import type { MoneyBreakdown } from '@/lib/utils';

// ==================== ENUMS ====================

export type CarrierType =
  'LTL' | 'FTL' | 'PARCEL' | 'AIR' | 'OCEAN' | 'RAIL' | 'INTERMODAL' | 'COURIER' | 'SPECIALIZED';

export type CarrierStatus =
  'ACTIVE' | 'INACTIVE' | 'PENDING_APPROVAL' | 'SUSPENDED' | 'BLACKLISTED';

export type ServiceType =
  | 'STANDARD'
  | 'EXPEDITED'
  | 'OVERNIGHT'
  | 'SAME_DAY'
  | 'ECONOMY'
  | 'DEDICATED'
  | 'TEMPERATURE_CONTROLLED'
  | 'HAZMAT'
  | 'OVERSIZED';

export type CarrierDocumentType =
  | 'INSURANCE_CERTIFICATE'
  | 'OPERATING_AUTHORITY'
  | 'W9'
  | 'SAFETY_RATING'
  | 'CONTRACT'
  | 'RATE_AGREEMENT'
  | 'OTHER';

// ==================== INTERFACES ====================

export interface CarrierAddress {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

export interface TmsCarrier {
  id: string;
  companyId: string;
  code: string;
  name: string;
  legalName?: string;
  type: CarrierType;
  status: CarrierStatus;
  primaryContact?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: CarrierAddress;
  mcNumber?: string;
  dotNumber?: string;
  scac?: string;
  usdotNumber?: string;
  taxId?: string;
  serviceTypes: ServiceType[];
  equipmentTypes: string[];
  hazmatCertified: boolean;
  temperatureControlled: boolean;
  maxPayloadWeight?: number;
  maxPayloadVolume?: number;
  serviceRegions: string[];
  operatingStates: string[];
  overallRating?: number;
  onTimeDeliveryRate?: number;
  damageRate?: number;
  claimRate?: number;
  paymentTermsDays: number;
  creditLimit?: number;
  insuranceMinimum?: number;
  portalEnabled: boolean;
  portalEmail?: string;
  portalUserId?: string;
  isActive: boolean;
  lanes?: TmsCarrierLane[];
  documents?: TmsCarrierDocument[];
  performanceRecords?: TmsCarrierPerformance[];
  _count?: {
    lanes: number;
    documents: number;
    performanceRecords: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface TmsCarrierLane {
  id: string;
  carrierId: string;
  originRegion?: string;
  originCity?: string;
  originState?: string;
  originZipRange?: string;
  originCountry: string;
  destinationRegion?: string;
  destinationCity?: string;
  destinationState?: string;
  destinationZipRange?: string;
  destinationCountry: string;
  serviceType: ServiceType;
  transitDaysMin?: number;
  transitDaysMax?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TmsCarrierDocument {
  id: string;
  carrierId: string;
  type: CarrierDocumentType;
  name: string;
  documentNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  documentUrl?: string;
  notes?: string;
  isVerified: boolean;
  verifiedById?: string;
  verifiedBy?: { id: string; fullName: string; email: string };
  verifiedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface TmsCarrierPerformance {
  id: string;
  carrierId: string;
  periodMonth: string;
  totalShipments: number;
  totalWeight?: number;
  totalRevenue?: number;
  onTimePickups: number;
  latePickups: number;
  onTimeDeliveries: number;
  lateDeliveries: number;
  damageIncidents: number;
  lossIncidents: number;
  claimsCount: number;
  claimsValue?: number;
  pickupPerformance?: number;
  deliveryPerformance?: number;
  qualityScore?: number;
  overallScore?: number;
  createdAt: string;
  updatedAt: string;
}

// ==================== DTOs ====================

export interface CreateCarrierDto {
  companyId: string;
  code: string;
  name: string;
  legalName?: string;
  type: CarrierType;
  primaryContact?: string;
  email?: string;
  phone?: string;
  website?: string;
  address?: CarrierAddress;
  mcNumber?: string;
  dotNumber?: string;
  scac?: string;
  usdotNumber?: string;
  taxId?: string;
  serviceTypes?: ServiceType[];
  equipmentTypes?: string[];
  hazmatCertified?: boolean;
  temperatureControlled?: boolean;
  maxPayloadWeight?: number;
  maxPayloadVolume?: number;
  serviceRegions?: string[];
  operatingStates?: string[];
  paymentTermsDays?: number;
  creditLimit?: number;
  insuranceMinimum?: number;
  portalEnabled?: boolean;
  portalEmail?: string;
}

export interface UpdateCarrierDto extends Partial<Omit<CreateCarrierDto, 'companyId' | 'code'>> {
  status?: CarrierStatus;
}

export interface QueryCarrierParams {
  companyId?: string;
  search?: string;
  type?: CarrierType;
  status?: CarrierStatus;
  serviceType?: ServiceType;
  isActive?: boolean;
  hazmatCertified?: boolean;
  temperatureControlled?: boolean;
  originState?: string;
  destinationState?: string;
  page?: number;
  limit?: number;
}

export interface CreateCarrierLaneDto {
  originRegion?: string;
  originCity?: string;
  originState?: string;
  originZipRange?: string;
  originCountry?: string;
  destinationRegion?: string;
  destinationCity?: string;
  destinationState?: string;
  destinationZipRange?: string;
  destinationCountry?: string;
  serviceType?: ServiceType;
  transitDaysMin?: number;
  transitDaysMax?: number;
  isActive?: boolean;
}

export interface CreateCarrierDocumentDto {
  type: CarrierDocumentType;
  name: string;
  documentNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  documentUrl?: string;
  notes?: string;
}

export interface CarriersSummary {
  totalCarriers: number;
  byStatus: Record<CarrierStatus, number>;
  byType: Record<CarrierType, number>;
  expiringDocuments: number;
  recentlyAdded: Pick<TmsCarrier, 'id' | 'code' | 'name' | 'type' | 'status' | 'createdAt'>[];
}

export interface CarrierPerformanceReport {
  carrierId: string;
  carrierName: string;
  monthlyRecords: TmsCarrierPerformance[];
  aggregate: {
    totalShipments: number;
    totalWeight: number;
    totalRevenue: number;
    onTimePickups: number;
    latePickups: number;
    onTimeDeliveries: number;
    lateDeliveries: number;
    damageIncidents: number;
    claimsCount: number;
    claimsValue: number;
    pickupPerformance: number | null;
    deliveryPerformance: number | null;
    damageRate: number | null;
  } | null;
}

// ==================== API SERVICE ====================

export const tmsCarriersApi = {
  // Carrier CRUD
  async getAll(params?: QueryCarrierParams) {
    const response = await api.get<{
      data: TmsCarrier[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/tms/carriers', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<TmsCarrier>(`/tms/carriers/${id}`);
    return response.data;
  },

  async getSummary(companyId: string) {
    const response = await api.get<CarriersSummary>('/tms/carriers/summary', {
      params: { companyId },
    });
    return response.data;
  },

  async create(data: CreateCarrierDto) {
    const response = await api.post<TmsCarrier>('/tms/carriers', data);
    return response.data;
  },

  async update(id: string, data: UpdateCarrierDto) {
    const response = await api.patch<TmsCarrier>(`/tms/carriers/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete<TmsCarrier>(`/tms/carriers/${id}`);
    return response.data;
  },

  // Carrier Status
  async approve(id: string) {
    const response = await api.post<TmsCarrier>(`/tms/carriers/${id}/approve`);
    return response.data;
  },

  async suspend(id: string, reason?: string) {
    const response = await api.post<TmsCarrier>(`/tms/carriers/${id}/suspend`, { reason });
    return response.data;
  },

  async reactivate(id: string) {
    const response = await api.post<TmsCarrier>(`/tms/carriers/${id}/reactivate`);
    return response.data;
  },

  // Lanes
  async getLanes(carrierId: string) {
    const response = await api.get<TmsCarrierLane[]>(`/tms/carriers/${carrierId}/lanes`);
    return response.data;
  },

  async addLane(carrierId: string, data: CreateCarrierLaneDto) {
    const response = await api.post<TmsCarrierLane>(`/tms/carriers/${carrierId}/lanes`, data);
    return response.data;
  },

  async updateLane(laneId: string, data: Partial<CreateCarrierLaneDto>) {
    const response = await api.patch<TmsCarrierLane>(`/tms/carriers/lanes/${laneId}`, data);
    return response.data;
  },

  async removeLane(carrierId: string, laneId: string) {
    const response = await api.delete<TmsCarrierLane>(`/tms/carriers/${carrierId}/lanes/${laneId}`);
    return response.data;
  },

  // Documents
  async getDocuments(carrierId: string) {
    const response = await api.get<TmsCarrierDocument[]>(`/tms/carriers/${carrierId}/documents`);
    return response.data;
  },

  async addDocument(carrierId: string, data: CreateCarrierDocumentDto) {
    const response = await api.post<TmsCarrierDocument>(
      `/tms/carriers/${carrierId}/documents`,
      data
    );
    return response.data;
  },

  async verifyDocument(documentId: string) {
    const response = await api.post<TmsCarrierDocument>(
      `/tms/carriers/documents/${documentId}/verify`
    );
    return response.data;
  },

  async removeDocument(carrierId: string, documentId: string) {
    const response = await api.delete<TmsCarrierDocument>(
      `/tms/carriers/${carrierId}/documents/${documentId}`
    );
    return response.data;
  },

  async getExpiringDocuments(carrierId: string, daysAhead?: number) {
    const response = await api.get<TmsCarrierDocument[]>(
      `/tms/carriers/${carrierId}/documents/expiring`,
      {
        params: { daysAhead },
      }
    );
    return response.data;
  },

  // Performance
  async getPerformance(carrierId: string, months?: number) {
    const response = await api.get<CarrierPerformanceReport>(
      `/tms/carriers/${carrierId}/performance`,
      {
        params: { months },
      }
    );
    return response.data;
  },
};

// ==================== FREIGHT RFQ ====================

// RFQ Enums
export type RFQStatus = 'Draft' | 'Open' | 'Closed' | 'Awarded' | 'Cancelled';
export type RFQType = 'Spot' | 'Annual' | 'NewLane' | 'Contract' | 'Mini_Bid';
export type ShipmentType =
  'FTL' | 'LTL' | 'OCEAN_FCL' | 'OCEAN_LCL' | 'AIR' | 'RAIL' | 'BREAK_BULK' | 'INTERMODAL';
export type Incoterm = 'EXW' | 'FOB' | 'CIF' | 'DDP' | 'DAP' | 'FCA' | 'CPT' | 'CIP' | 'CFR';
export type ContainerType =
  'GP20' | 'GP40' | 'HQ40' | 'RF20' | 'RF40' | 'OT20' | 'OT40' | 'FR20' | 'FR40';
export type TransportMode = 'Ocean' | 'Air' | 'TruckFTL' | 'TruckLTL' | 'Rail' | 'Intermodal';
export type InvitationStatus = 'Pending' | 'Sent' | 'Viewed' | 'Responded' | 'Declined' | 'Expired';
export type ResponseStatus = 'Draft' | 'Submitted' | 'Accepted' | 'Rejected' | 'Withdrawn';
export type AwardStatus = 'Pending' | 'Reviewed' | 'Published' | 'Rejected';
export type RateType =
  'Flat' | 'PerMile' | 'PerCWT' | 'PerPallet' | 'PerUnit' | 'PerContainer' | 'PerKg' | 'PerCBM';

// RFQ Interfaces
export interface FreightRFQ {
  id: string;
  rfqNumber: string;
  title: string;
  description?: string;
  status: RFQStatus;
  type: RFQType;
  shipmentType?: ShipmentType;
  incoterm?: Incoterm;
  containerTypes?: ContainerType[];
  issueDate?: string;
  responseDeadline: string;
  awardTargetDate?: string;
  rateEffectiveDate?: string;
  rateExpirationDate?: string;
  estimatedAnnualVolume?: number;
  volumeCommitmentType?: string;
  cargoDescription?: string;
  hazmatRequired: boolean;
  oversizeRequired: boolean;
  temperatureControlled: boolean;
  avgWeightLbs?: number;
  maxBidSubmissions?: number;
  allowBidRevision: boolean;
  totalQuotesReceived: number;
  lowestBidAmount?: number;
  highestBidAmount?: number;
  companyId: string;
  createdById: string;
  createdBy?: { id: string; fullName: string; email: string };
  lines?: FreightRFQLine[];
  invitations?: FreightRFQInvitation[];
  _count?: {
    lines: number;
    invitations: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface FreightRFQLine {
  id: string;
  rfqId: string;
  lineNumber: number;
  originCity?: string;
  originState?: string;
  originZip?: string;
  originCountry?: string;
  originPort?: string;
  destCity?: string;
  destState?: string;
  destZip?: string;
  destCountry?: string;
  destPort?: string;
  mode?: TransportMode;
  serviceLevel?: string;
  equipmentType?: string;
  weight?: number;
  volume?: number;
  pieces?: number;
  commodityDescription?: string;
  hazmatClass?: string;
  pickupDateRange?: string;
  deliveryDateRange?: string;
  transitDaysTarget?: number;
  specialRequirements?: string;
  targetRate?: number;
  responses?: FreightRFQResponse[];
  award?: FreightRFQAward;
  _count?: {
    responses: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface FreightRFQInvitation {
  id: string;
  rfqId: string;
  carrierId: string;
  carrier: TmsCarrier;
  status: InvitationStatus;
  invitedAt: string;
  sentAt?: string;
  viewedAt?: string;
  respondedAt?: string;
  declinedReason?: string;
  responses?: FreightRFQResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface FreightRFQResponse {
  id: string;
  invitationId: string;
  rfqLineId: string;
  status: ResponseStatus;
  quoteStatus?: string;
  version: number;
  isLatestVersion: boolean;
  submitted: boolean;
  submittedAt?: string;
  submissionCount: number;
  withdrawnAt?: string;
  withdrawnReason?: string;
  rateType: RateType;
  baseRate: number;
  totalRate?: number;
  currency: string;
  isContractRate?: boolean;
  fuelSurchargeType?: string;
  fuelSurchargeValue?: number;
  accessorialRates?: Record<string, unknown>;
  proposedEffectiveDate: string;
  proposedExpirationDate: string;
  transitDaysOffered: number;
  capacityGuarantee?: string;
  notes?: string;
  childRates?: RFQResponseChildRate[];
  slabRates?: RFQResponseSlabRate[];
  invitation?: FreightRFQInvitation;
  rfqLine?: FreightRFQLine;
  createdAt: string;
  updatedAt: string;
}

export interface RFQResponseChildRate {
  id: string;
  responseId: string;
  chargeType: string;
  amount: number;
  currency: string;
  rateUnit?: string;
  isOptional: boolean;
}

export interface RFQResponseSlabRate {
  id: string;
  responseId: string;
  minValue: number;
  maxValue?: number;
  rate: number;
  rateType: string;
  unit: string;
}

export interface FreightRFQAward {
  id: string;
  rfqId: string;
  rfqLineId: string;
  carrierId: string;
  carrier?: TmsCarrier;
  responseId: string;
  awardedById: string;
  awardedBy?: { id: string; fullName: string };
  awardedRateType: RateType;
  awardedBaseRate: number;
  awardedFuelType?: string;
  awardedFuelValue?: number;
  effectiveDate: string;
  expirationDate: string;
  notes?: string;
  status: AwardStatus;
  awardedAt?: string;
  reviewedById?: string;
  reviewedAt?: string;
  publishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// RFQ DTOs
export interface CreateRFQDto {
  title: string;
  description?: string;
  type?: RFQType;
  shipmentType?: ShipmentType;
  incoterm?: Incoterm;
  containerTypes?: ContainerType[];
  issueDate?: string;
  responseDeadline: string;
  awardTargetDate?: string;
  rateEffectiveDate: string;
  rateExpirationDate: string;
  estimatedAnnualVolume?: number;
  volumeCommitmentType?: string;
  cargoDescription?: string;
  hazmatRequired?: boolean;
  oversizeRequired?: boolean;
  temperatureControlled?: boolean;
  temperatureMin?: number;
  temperatureMax?: number;
  avgWeightLbs?: number;
  baseCurrency?: string;
  maxBidSubmissions?: number;
  allowBidRevision?: boolean;
  workScope?: string;
  specialInstructions?: string;
  rateTemplateId?: string;
}

export interface UpdateRFQDto extends Partial<CreateRFQDto> {
  status?: RFQStatus;
}

export interface CreateRFQLineDto {
  lineNumber?: number;
  originCity?: string;
  originState?: string;
  originZip?: string;
  originCountry?: string;
  originPort?: string;
  destCity?: string;
  destState?: string;
  destZip?: string;
  destCountry?: string;
  destPort?: string;
  mode?: TransportMode;
  containerType?: ContainerType;
  containerCount?: number;
  serviceLevel?: string;
  equipmentType?: string;
  weight?: number;
  volume?: number;
  pieces?: number;
  estimatedShipments?: number;
  estimatedWeight?: number;
  commodityDescription?: string;
  hazmatClass?: string;
  pickupDateRange?: string;
  deliveryDateRange?: string;
  transitDaysTarget?: number;
  specialRequirements?: string;
  currentRate?: number;
  targetRate?: number;
}

export interface InviteCarriersDto {
  carrierIds: string[];
  message?: string;
}

export interface SubmitQuoteDto {
  rateType: RateType;
  baseRate: number;
  currency?: string;
  fuelSurchargeType?: string;
  fuelSurchargeValue?: number;
  accessorialRates?: Record<string, unknown>;
  proposedEffectiveDate: string;
  proposedExpirationDate: string;
  transitDaysOffered: number;
  capacityGuarantee?: string;
  notes?: string;
  childRates?: Omit<RFQResponseChildRate, 'id' | 'responseId'>[];
  slabRates?: Omit<RFQResponseSlabRate, 'id' | 'responseId'>[];
}

export interface AwardResponseDto {
  notes?: string;
}

export interface QueryRFQParams {
  status?: RFQStatus;
  type?: RFQType;
  search?: string;
  fromDate?: string;
  toDate?: string;
  page?: number;
  limit?: number;
}

// RFQ API Service
export const rfqApi = {
  // RFQ CRUD
  async getAll(params?: QueryRFQParams) {
    const response = await api.get<{
      data: FreightRFQ[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/tms/rfq', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<FreightRFQ>(`/tms/rfq/${id}`);
    return response.data;
  },

  async create(data: CreateRFQDto) {
    const response = await api.post<FreightRFQ>('/tms/rfq', data);
    return response.data;
  },

  async update(id: string, data: UpdateRFQDto) {
    const response = await api.patch<FreightRFQ>(`/tms/rfq/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete<void>(`/tms/rfq/${id}`);
    return response.data;
  },

  // RFQ Status Actions
  /**
   * Opens an RFQ to its invited carriers.
   *
   * This posted to `/send`, which has never existed — so the primary action of
   * the whole RFQ module answered 404 while `/open`, the transition the server
   * actually implements, sat unused beside it.
   */
  async send(id: string) {
    const response = await api.post<FreightRFQ>(`/tms/rfq/${id}/open`);
    return response.data;
  },

  async close(id: string) {
    const response = await api.post<FreightRFQ>(`/tms/rfq/${id}/close`);
    return response.data;
  },

  async cancel(id: string, reason?: string) {
    const response = await api.post<FreightRFQ>(`/tms/rfq/${id}/cancel`, { reason });
    return response.data;
  },

  // Lines
  async getLines(rfqId: string) {
    const response = await api.get<FreightRFQLine[]>(`/tms/rfq/${rfqId}/lines`);
    return response.data;
  },

  async addLine(rfqId: string, data: CreateRFQLineDto) {
    const response = await api.post<FreightRFQLine>(`/tms/rfq/${rfqId}/lines`, data);
    return response.data;
  },

  async updateLine(rfqId: string, lineId: string, data: Partial<CreateRFQLineDto>) {
    const response = await api.patch<FreightRFQLine>(`/tms/rfq/${rfqId}/lines/${lineId}`, data);
    return response.data;
  },

  async removeLine(rfqId: string, lineId: string) {
    const response = await api.delete<void>(`/tms/rfq/${rfqId}/lines/${lineId}`);
    return response.data;
  },

  // Invitations
  async getInvitations(rfqId: string) {
    const response = await api.get<FreightRFQInvitation[]>(`/tms/rfq/${rfqId}/invitations`);
    return response.data;
  },

  async inviteCarriers(rfqId: string, data: InviteCarriersDto) {
    const response = await api.post<FreightRFQInvitation[]>(`/tms/rfq/${rfqId}/invitations`, data);
    return response.data;
  },

  async removeInvitation(rfqId: string, invitationId: string) {
    const response = await api.delete<void>(`/tms/rfq/${rfqId}/invitations/${invitationId}`);
    return response.data;
  },

  // Responses (Quotes)
  async getResponses(rfqId: string, lineId?: string) {
    const params = lineId ? { lineId } : undefined;
    const response = await api.get<FreightRFQResponse[]>(`/tms/rfq/${rfqId}/responses`, { params });
    return response.data;
  },

  async getResponseById(rfqId: string, responseId: string) {
    const response = await api.get<FreightRFQResponse>(`/tms/rfq/${rfqId}/responses/${responseId}`);
    return response.data;
  },

  // Awards
  async getAwards(rfqId: string) {
    const response = await api.get<FreightRFQAward[]>(`/tms/rfq/${rfqId}/awards`);
    return response.data;
  },

  async awardResponse(rfqId: string, lineId: string, responseId: string, data: AwardResponseDto) {
    const response = await api.post<FreightRFQAward>(`/tms/rfq/${rfqId}/lines/${lineId}/award`, {
      responseId,
      ...data,
    });
    return response.data;
  },

  async rejectResponse(rfqId: string, lineId: string, responseId: string, reason?: string) {
    const response = await api.post<void>(`/tms/rfq/${rfqId}/lines/${lineId}/reject`, {
      responseId,
      reason,
    });
    return response.data;
  },

  // Analytics
  async getAnalytics(rfqId: string) {
    const response = await api.get<{
      totalCarriersInvited: number;
      totalResponses: number;
      responseRate: number;
      avgBaseRate: number;
      lowestRate: number;
      highestRate: number;
      byCarrier: { carrierId: string; carrierName: string; responses: number; avgRate: number }[];
      byLine: { lineId: string; lineNumber: number; responses: number; avgRate: number }[];
    }>(`/tms/rfq/${rfqId}/analytics`);
    return response.data;
  },

  // Compare quotes for a line
  async compareQuotes(rfqId: string, lineId: string) {
    // The endpoint returns `responses`, not `quotes`; the mismatched name meant
    // the comparison page read undefined and threw before rendering a row.
    const response = await api.get<{
      responses: (FreightRFQResponse & { carrier: TmsCarrier })[];
      lowestRate: number | null;
      responseCount: number;
    }>(`/tms/rfq/${rfqId}/lines/${lineId}/compare`);
    return response.data;
  },

  // Create award directly
  async createAward(
    rfqId: string,
    data: { lineId: string; responseId: string; awardedRate: number; notes?: string }
  ) {
    const response = await api.post<FreightRFQAward>(`/tms/rfq/${rfqId}/awards`, data);
    return response.data;
  },

  // Approvals
  async getMyPendingApprovals() {
    const response = await api.get<PendingApproval[]>('/tms/rfq/approval/my-pending');
    return response.data;
  },

  async processApproval(requestId: string, action: 'Approve' | 'Reject', comment?: string) {
    const response = await api.post<void>(`/tms/rfq/approval/requests/${requestId}/process`, {
      // ProcessApprovalDto expects `decision`; sending `action` is rejected by
      // the whitelisting validation pipe.
      decision: action,
      comment,
    });
    return response.data;
  },

  // Approval Config
  async getApprovalConfig(rfqId: string) {
    const response = await api.get<ApprovalConfig | null>(`/tms/rfq/${rfqId}/approval/config`);
    return response.data;
  },

  async createOrUpdateApprovalConfig(rfqId: string, dto: CreateApprovalConfigDto) {
    const response = await api.post<ApprovalConfig>(`/tms/rfq/${rfqId}/approval/config`, dto);
    return response.data;
  },

  async getApprovalStatus(rfqId: string) {
    const response = await api.get<RFQApprovalStatus>(`/tms/rfq/${rfqId}/approval/status`);
    return response.data;
  },

  async getAllApprovalConfigs() {
    const response = await api.get<ApprovalConfig[]>('/tms/rfq/approval/all-configs');
    return response.data;
  },

  async getApproverCandidates() {
    const response = await api.get<ApproverCandidate[]>('/tms/rfq/approval/approver-candidates');
    return response.data;
  },

  async requestApproval(rfqId: string, awardId: string) {
    const response = await api.post<{ message: string; levels: number }>(
      `/tms/rfq/${rfqId}/awards/${awardId}/request-approval`
    );
    return response.data;
  },

  // Quote Version History
  async getQuoteVersionHistory(rfqId: string, lineId: string, carrierId: string) {
    const response = await api.get<QuoteVersionHistory>(
      `/tms/rfq/${rfqId}/lines/${lineId}/quotes/${carrierId}/history`
    );
    return response.data;
  },

  // Negotiations
  async getNegotiations(rfqId: string) {
    const response = await api.get<RFQNegotiation[]>(`/tms/rfq/${rfqId}/negotiations`);
    return response.data;
  },

  async getNegotiationById(rfqId: string, negotiationId: string) {
    const response = await api.get<RFQNegotiation>(
      `/tms/rfq/${rfqId}/negotiations/${negotiationId}`
    );
    return response.data;
  },

  async startNegotiation(rfqId: string, dto: CreateNegotiationDto) {
    const response = await api.post<RFQNegotiation>(`/tms/rfq/${rfqId}/negotiations`, dto);
    return response.data;
  },

  async acceptCounterOffer(rfqId: string, negotiationId: string) {
    const response = await api.post<RFQNegotiation>(
      `/tms/rfq/${rfqId}/negotiations/${negotiationId}/accept-counter`
    );
    return response.data;
  },

  async counterCounterOffer(
    rfqId: string,
    negotiationId: string,
    targetRate: number,
    message?: string
  ) {
    const response = await api.post<RFQNegotiation>(
      `/tms/rfq/${rfqId}/negotiations/${negotiationId}/counter`,
      { targetRate, message }
    );
    return response.data;
  },

  async rejectCounterOffer(rfqId: string, negotiationId: string, message?: string) {
    const response = await api.post<RFQNegotiation>(
      `/tms/rfq/${rfqId}/negotiations/${negotiationId}/reject`,
      { message }
    );
    return response.data;
  },

  async getNegotiationAnalytics(rfqId: string) {
    const response = await api.get<NegotiationAnalytics>(
      `/tms/rfq/${rfqId}/negotiations/analytics`
    );
    return response.data;
  },

  // RFQ Analytics
  async getRFQAnalytics(filter?: RFQAnalyticsFilter) {
    const params = new URLSearchParams();
    if (filter?.dateRange) params.append('dateRange', filter.dateRange);
    if (filter?.startDate) params.append('startDate', filter.startDate);
    if (filter?.endDate) params.append('endDate', filter.endDate);
    if (filter?.shipmentType) params.append('shipmentType', filter.shipmentType);

    const response = await api.get<RFQAnalyticsData>(`/tms/rfq/analytics?${params.toString()}`);
    return response.data;
  },

  async getVendorAnalytics(filter?: RFQAnalyticsFilter) {
    const params = new URLSearchParams();
    if (filter?.dateRange) params.append('dateRange', filter.dateRange);

    const response = await api.get<VendorParticipation[]>(
      `/tms/rfq/analytics/vendors?${params.toString()}`
    );
    return response.data;
  },

  async getLaneAnalytics(filter?: RFQAnalyticsFilter) {
    const params = new URLSearchParams();
    if (filter?.dateRange) params.append('dateRange', filter.dateRange);

    const response = await api.get<TopLane[]>(`/tms/rfq/analytics/lanes?${params.toString()}`);
    return response.data;
  },

  async getPortAnalytics(filter?: RFQAnalyticsFilter) {
    const params = new URLSearchParams();
    if (filter?.dateRange) params.append('dateRange', filter.dateRange);

    const response = await api.get<PortAnalytics>(`/tms/rfq/analytics/ports?${params.toString()}`);
    return response.data;
  },
};

// Pending Approval interface
export interface PendingApproval {
  id: string;
  requestId: string;
  rfqId: string;
  rfqNumber: string;
  rfqTitle: string;
  rfqType: string;
  shipmentType?: string;
  lineNumber: number;
  originCity: string;
  destCity: string;
  carrierId: string;
  carrierName: string;
  carrierCode: string;
  awardedRate: number;
  levelNumber: number;
  levelName: string;
  requestedAt: string;
  requestedBy?: string;
  status: 'Pending' | 'Approved' | 'Rejected';
}

// ==================== QUOTE VERSION HISTORY ====================

export interface QuoteVersionHistory {
  carrierId: string;
  carrierName: string;
  carrierCode: string;
  lineId: string;
  totalVersions: number;
  versions: QuoteVersion[];
}

export interface QuoteVersion {
  id: string;
  version: number;
  submittedAt: string | null;
  submittedBy: string | null;
  quoteStatus: string;
  baseRate: number;
  totalRate: number | null;
  currency: string;
  transitDaysOffered: number | null;
  capacityGuarantee: string | null;
  isContractRate: boolean;
  fuelSurchargeType: string | null;
  fuelSurchargeValue: number | null;
  childRatesTotal: number;
  childRatesCount: number;
  childRates: RFQResponseChildRate[];
  isLatestVersion: boolean;
  isCurrentlyAwarded: boolean;
  changes: QuoteVersionChanges | null;
}

export interface QuoteVersionChanges {
  baseRateChange: number;
  baseRateChangePercent: number | null;
  totalRateChange: number;
  totalRateChangePercent: number | null;
  transitDaysChange: number | null;
  newChargesAdded: string[];
  chargesRemoved: string[];
}

// ==================== NEGOTIATIONS ====================

export type NegotiationStatus =
  'Pending' | 'InProgress' | 'Accepted' | 'Rejected' | 'CounterOffered' | 'Expired';

export type NegotiationParty = 'Shipper' | 'Carrier';

export interface RFQNegotiation {
  id: string;
  responseId: string;
  rfqId: string | null;
  lineId: string | null;
  round: number;
  initiatedBy: NegotiationParty;
  targetRate: number;
  finalRate: number | null;
  message: string | null;
  deadline: string | null;
  counterRate: number | null;
  counterMessage: string | null;
  respondedAt: string | null;
  metadata: {
    history?: {
      round: number;
      shipperRate: number;
      carrierCounter: number | null;
      carrierMessage: string | null;
      timestamp: string;
    }[];
  } | null;
  status: NegotiationStatus;
  createdById: string;
  createdBy?: { id: string; fullName: string };
  createdAt: string;
  updatedAt: string;
  // Computed fields from API
  carrierName: string;
  savingsPercent: number | null;
  // Included relations
  line?: {
    lineNumber: number;
    originCity: string | null;
    originState: string | null;
    destCity: string | null;
    destState: string | null;
  };
  rfq?: {
    rfqNumber: string;
    title: string;
  };
  response?: FreightRFQResponse;
}

export interface CreateNegotiationDto {
  lineId: string;
  responseId: string;
  targetRate: number;
  deadline: string;
  message?: string;
}

export interface RespondNegotiationDto {
  action: 'Accept' | 'Reject' | 'Counter';
  counterRate?: number;
  message?: string;
}

export interface NegotiationAnalytics {
  totalNegotiations: number;
  acceptedCount: number;
  rejectedCount: number;
  pendingCount: number;
  totalSavings: number;
  averageSavingsPercent: number;
  averageRounds: number;
  successRate: number;
}

// ==================== APPROVAL CONFIG ====================

export type ApprovalFlowType = 'Sequential' | 'Parallel';
export type ApprovalLevelLogic = 'Anyone' | 'Everyone';
export type ApprovalRuleType =
  'AmountThreshold' | 'ContractRate' | 'ShipmentType' | 'UserBased' | 'LaneSpecific';
export type ApprovalActionType =
  | 'PublishToRateEngine'
  | 'SendNotification'
  | 'UpdateRFQStatus'
  | 'CreateShipment'
  | 'ResetAwardStatus'
  | 'NotifyCarrier'
  | 'LogEvent';

export interface ApprovalRule {
  id?: string;
  ruleType: ApprovalRuleType;
  condition: Record<string, any>;
}

export interface ApprovalAction {
  type: ApprovalActionType;
  template?: string;
  status?: string;
  metadata?: Record<string, any>;
}

export interface ApprovalLevelApprover {
  id: string;
  userId: string;
  user?: {
    id: string;
    fullName: string;
    email: string;
  };
}

export interface ApprovalLevel {
  id?: string;
  levelNumber: number;
  name: string;
  logic: ApprovalLevelLogic;
  approvers: ApprovalLevelApprover[];
  approverIds?: string[];
  rules: ApprovalRule[];
  requests?: {
    id: string;
    status: string;
    decidedAt: string | null;
    approverId: string | null;
    comments: string | null;
  }[];
}

export interface ApprovalConfig {
  id: string;
  rfqId: string;
  companyId: string;
  isEnabled: boolean;
  flowType: ApprovalFlowType;
  levels: ApprovalLevel[];
  onApprovalActions: ApprovalAction[];
  onRejectionActions: ApprovalAction[];
  createdAt: string;
  updatedAt: string;
  rfq?: {
    id: string;
    rfqNumber: string;
    title: string;
    status: string;
  };
}

export interface CreateApprovalConfigDto {
  isEnabled?: boolean;
  flowType?: ApprovalFlowType;
  levels: {
    levelNumber: number;
    name: string;
    logic?: ApprovalLevelLogic;
    approverIds: string[];
    rules?: {
      ruleType: ApprovalRuleType;
      condition: Record<string, any>;
    }[];
  }[];
  onApprovalActions?: ApprovalAction[];
  onRejectionActions?: ApprovalAction[];
}

export interface RFQApprovalStatus {
  isConfigured: boolean;
  isEnabled?: boolean;
  flowType?: ApprovalFlowType;
  levels: {
    levelNumber: number;
    name: string;
    logic: ApprovalLevelLogic;
    approverCount: number;
    status: string;
    decidedAt: string | null;
    approverId: string | null;
    comments: string | null;
  }[];
  isComplete?: boolean;
}

export interface ApproverCandidate {
  id: string;
  fullName: string;
  email: string;
  userRoles: {
    role: { name: string };
  }[];
}

// ==================== RFQ ANALYTICS ====================

export interface RFQAnalyticsSummary {
  totalRFQs: number;
  openRFQs: number;
  awardedRFQs: number;
  /** Null when awards span currencies with no rate on file. */
  totalValue: number | null;
  currency: string;
  valueByCurrency: MoneyBreakdown[];
  avgTimeToAward: number;
  /**
   * Null until savings are actually computed. This was a hardcoded 8.3 on the
   * server, shown to customers as "8.3% average savings" — a number nobody
   * calculated, on the screen whose job is to report what procurement achieved.
   */
  avgSavingsPercent: number | null;
}

export interface RFQStatusBreakdown {
  status: string;
  count: number;
  value: number | null;
  currency: string;
  valueByCurrency: MoneyBreakdown[];
}

export interface VendorParticipation {
  carrierId: string;
  carrierName: string;
  carrierCode: string;
  invitations: number;
  responded: number;
  awarded: number;
  totalValue: number | null;
  currency: string;
  valueByCurrency: MoneyBreakdown[];
  responseRate: number;
  winRate: number;
}

export interface TopLane {
  originCity: string;
  originCountry: string;
  destCity: string;
  destCountry: string;
  rfqCount: number;
  totalValue: number | null;
  currency: string;
  valueByCurrency: MoneyBreakdown[];
  avgRate: number;
}

export interface MonthlyTrend {
  month: string;
  rfqCount: number;
  awardedCount: number;
  totalValue: number | null;
  currency: string;
  valueByCurrency: MoneyBreakdown[];
}

export interface TimeToAwardDistribution {
  range: string;
  count: number;
}

export interface PortAnalytics {
  topOriginPorts: Array<{
    port: string;
    country: string;
    rfqCount: number;
    totalValue: number | null;
    currency: string;
    valueByCurrency: MoneyBreakdown[];
  }>;
  topDestinationPorts: Array<{
    port: string;
    country: string;
    rfqCount: number;
    totalValue: number | null;
    currency: string;
    valueByCurrency: MoneyBreakdown[];
  }>;
  portPairs: Array<{
    originPort: string;
    destPort: string;
    rfqCount: number;
    totalValue: number | null;
    currency: string;
    valueByCurrency: MoneyBreakdown[];
    avgRate: number;
  }>;
}

export interface RFQAnalyticsData {
  summary: RFQAnalyticsSummary;
  statusBreakdown: RFQStatusBreakdown[];
  vendorParticipation: VendorParticipation[];
  topLanes: TopLane[];
  monthlyTrends: MonthlyTrend[];
  timeToAwardDistribution: TimeToAwardDistribution[];
  portAnalytics?: PortAnalytics;
}

export interface RFQAnalyticsFilter {
  dateRange?: '1m' | '3m' | '6m' | '1y' | 'all';
  startDate?: string;
  endDate?: string;
  shipmentType?: string;
}

// ==================== RATE ENGINE ====================

export type ChargeCategory =
  | 'BASE_FREIGHT'
  | 'FUEL'
  | 'ACCESSORIAL'
  | 'HANDLING'
  | 'DOCUMENTATION'
  | 'CUSTOMS'
  | 'INSURANCE'
  | 'OTHER';

export interface CarrierRate {
  id: string;
  rateSKU?: string;
  carrierId: string;
  carrier?: TmsCarrier;
  laneId?: string;
  lane?: {
    id: string;
    originCity?: string;
    originState?: string;
    originCountry?: string;
    destCity?: string;
    destState?: string;
    destCountry?: string;
  };
  mode?: TransportMode;
  chargeCategory: ChargeCategory;
  chargeCode: string;
  chargeName: string;
  rateType: RateType;
  baseRate: number;
  currency: string;
  effectiveDate: string;
  expirationDate?: string;
  isActive: boolean;
  isContractRate: boolean;
  minCharge?: number;
  maxCharge?: number;
  fuelSurchargePercent?: number;
  version: number;
  commodity?: string;
  notes?: string;
  childRates?: RateChildCharge[];
  slabs?: RateSlab[];
  createdAt?: string;
  updatedAt?: string;
}

export interface RateChildCharge {
  id: string;
  rateId: string;
  chargeCode: string;
  chargeName: string;
  chargeCategory: ChargeCategory;
  rateType: RateType;
  amount: number;
  currency: string;
  isMandatory: boolean;
  notes?: string;
}

export interface RateSlab {
  id: string;
  rateId: string;
  minValue: number;
  maxValue?: number;
  rate: number;
  unit: string;
}

export interface RateFilterDto {
  page?: number;
  limit?: number;
  search?: string;
  carrierId?: string;
  mode?: TransportMode;
  chargeCategory?: ChargeCategory;
  commodity?: string;
  activeOnly?: boolean;
  expiringSoon?: boolean;
  contractOnly?: boolean;
  originCity?: string;
  originState?: string;
  destCity?: string;
  destState?: string;
}

export interface CreateRateDto {
  carrierId: string;
  laneId?: string;
  mode?: TransportMode;
  chargeCategory: ChargeCategory;
  chargeCode: string;
  chargeName: string;
  rateType: RateType;
  baseRate: number;
  currency?: string;
  effectiveDate: string;
  expirationDate?: string;
  isContractRate?: boolean;
  minCharge?: number;
  maxCharge?: number;
  fuelSurchargePercent?: number;
  commodity?: string;
  notes?: string;
  childRates?: Omit<RateChildCharge, 'id' | 'rateId'>[];
  slabs?: Omit<RateSlab, 'id' | 'rateId'>[];
}

export interface UpdateRateDto extends Partial<CreateRateDto> {
  isActive?: boolean;
}

export interface RateVersion {
  id: string;
  rateId: string;
  version: number;
  baseRate: number;
  effectiveDate: string;
  expirationDate?: string;
  changedBy?: string;
  changeReason?: string;
  createdAt: string;
}

export const rateEngineApi = {
  // Get rates with filtering
  async getRates(filters?: RateFilterDto) {
    const response = await api.get<{
      data: CarrierRate[];
      pagination?: { total: number; page: number; limit: number };
    }>('/tms/rates', { params: filters });
    return response.data;
  },

  // Get single rate
  async getById(id: string) {
    const response = await api.get<CarrierRate>(`/tms/rates/${id}`);
    return response.data;
  },

  // Create rate
  async create(data: CreateRateDto) {
    const response = await api.post<CarrierRate>('/tms/rates', data);
    return response.data;
  },

  // Update rate
  async updateRate(id: string, data: UpdateRateDto) {
    const response = await api.patch<CarrierRate>(`/tms/rates/${id}`, data);
    return response.data;
  },

  // Duplicate rate
  async duplicateRate(id: string) {
    const response = await api.post<CarrierRate>(`/tms/rates/${id}/duplicate`);
    return response.data;
  },

  // Get rate version history
  async getVersionHistory(id: string) {
    const response = await api.get<RateVersion[]>(`/tms/rates/${id}/versions`);
    return response.data;
  },

  // Rollback to version
  async rollbackToVersion(id: string, version: number) {
    const response = await api.post<CarrierRate>(`/tms/rates/${id}/rollback`, { version });
    return response.data;
  },

  // Delete rate
  async delete(id: string) {
    const response = await api.delete<void>(`/tms/rates/${id}`);
    return response.data;
  },

  // Bulk import
  async bulkImport(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<{
      imported: number;
      failed: number;
      errors: { row: number; error: string }[];
    }>('/tms/rates/import', formData);
    return response.data;
  },

  // Get summary analytics
  async getSummary() {
    const response = await api.get<{
      totalRates: number;
      activeRates: number;
      expiringRates: number;
      contractRates: number;
      byMode: { mode: string; count: number }[];
      byCategory: { category: string; count: number }[];
    }>('/tms/rates/summary');
    return response.data;
  },
};

// Carrier API alias (uses TmsCarrier type)
export type Carrier = TmsCarrier;
export const carrierApi = tmsCarriersApi;

// ==================== CHARGE TYPES ====================

export type ChargeCategoryExtended =
  | 'Freight'
  | 'OriginCharges'
  | 'DestinationCharges'
  | 'OriginHaulage'
  | 'DestinationHaulage'
  | 'ExportClearance'
  | 'ImportClearance'
  | 'Documentation'
  | 'Insurance';

export type RateUnit =
  | 'PerContainer'
  | 'PerCBM'
  | 'PerKG'
  | 'PerMile'
  | 'PerCWT'
  | 'PerTon'
  | 'PerPallet'
  | 'PerUnit'
  | 'PerShipment'
  | 'Flat'
  | 'Percentage';

export interface ChargeType {
  id: string;
  code: string;
  name: string;
  category: ChargeCategoryExtended;
  description?: string;
  rateUnit?: RateUnit;
  isActive: boolean;
  isForRFQ: boolean;
  sortOrder?: number;
  companyId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateChargeTypeDto {
  code: string;
  name: string;
  category: ChargeCategoryExtended;
  description?: string;
  rateUnit?: RateUnit;
  isForRFQ?: boolean;
  sortOrder?: number;
}

export interface QueryChargeTypeParams {
  search?: string;
  category?: ChargeCategoryExtended;
  isActive?: boolean;
  isForRFQ?: boolean;
  page?: number;
  limit?: number;
}

export const chargeTypesApi = {
  async getAll(params?: QueryChargeTypeParams) {
    const response = await api.get<{
      data: ChargeType[];
      meta: { total: number; page: number; limit: number };
    }>('/tms/charge-types', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<ChargeType>(`/tms/charge-types/${id}`);
    return response.data;
  },

  async create(data: CreateChargeTypeDto) {
    const response = await api.post<ChargeType>('/tms/charge-types', data);
    return response.data;
  },

  async update(id: string, data: Partial<CreateChargeTypeDto>) {
    const response = await api.patch<ChargeType>(`/tms/charge-types/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete<void>(`/tms/charge-types/${id}`);
    return response.data;
  },

  /**
   * There is no toggle-active endpoint; the caller knows the current state, so
   * the flip goes through the ordinary update.
   */
  async toggleActive(id: string, isActive: boolean) {
    const response = await api.patch<ChargeType>(`/tms/charge-types/${id}`, {
      isActive: !isActive,
    });
    return response.data;
  },

  async seedDefaults() {
    const response = await api.post<{ created: number }>('/tms/charge-types/seed-defaults');
    return response.data;
  },
};

// ==================== VENDOR GROUPS ====================

export type VendorGroupRegion =
  | 'North America'
  | 'South America'
  | 'Europe'
  | 'Asia Pacific'
  | 'Middle East'
  | 'Africa'
  | 'Global';

export interface VendorGroup {
  id: string;
  name: string;
  description?: string;
  shipmentTypes?: ShipmentType[];
  regions?: VendorGroupRegion[];
  isActive: boolean;
  companyId: string;
  carriers: VendorGroupCarrier[];
  _count?: { carriers: number };
  createdAt: string;
  updatedAt: string;
}

export interface VendorGroupCarrier {
  id: string;
  groupId: string;
  carrierId: string;
  carrier: TmsCarrier;
  isPrimary: boolean;
  createdAt: string;
}

export interface CreateVendorGroupDto {
  name: string;
  description?: string;
  shipmentTypes?: ShipmentType[];
  regions?: VendorGroupRegion[];
}

export interface QueryVendorGroupParams {
  search?: string;
  shipmentType?: ShipmentType;
  region?: VendorGroupRegion;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface VendorGroupSummary {
  totalGroups: number;
  activeGroups: number;
  totalMembers: number;
  avgMembersPerGroup: number;
}

export const vendorGroupsApi = {
  async getAll(params?: QueryVendorGroupParams) {
    const response = await api.get<{
      data: VendorGroup[];
      meta: { total: number; page: number; limit: number };
    }>('/tms/vendor-groups', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<VendorGroup>(`/tms/vendor-groups/${id}`);
    return response.data;
  },

  async create(data: CreateVendorGroupDto) {
    const response = await api.post<VendorGroup>('/tms/vendor-groups', data);
    return response.data;
  },

  async update(id: string, data: Partial<CreateVendorGroupDto & { isActive?: boolean }>) {
    const response = await api.patch<VendorGroup>(`/tms/vendor-groups/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete<void>(`/tms/vendor-groups/${id}`);
    return response.data;
  },

  async addCarriers(groupId: string, carrierIds: string[]) {
    const response = await api.post<VendorGroup>(`/tms/vendor-groups/${groupId}/carriers`, {
      carrierIds,
    });
    return response.data;
  },

  async removeCarrier(groupId: string, carrierId: string) {
    const response = await api.delete<void>(`/tms/vendor-groups/${groupId}/carriers/${carrierId}`);
    return response.data;
  },

  async setPrimaryCarrier(groupId: string, carrierId: string) {
    const response = await api.post<VendorGroup>(`/tms/vendor-groups/${groupId}/primary`, {
      carrierId,
    });
    return response.data;
  },

  async getSummary() {
    const response = await api.get<VendorGroupSummary>('/tms/vendor-groups/summary');
    return response.data;
  },
};

// ==================== TERMS & CONDITIONS ====================

export interface TermGroup {
  id: string;
  name: string;
  description?: string;
  shipmentTypes?: ShipmentType[];
  regions?: string[];
  isDefault: boolean;
  isActive: boolean;
  companyId: string;
  terms: TermTemplate[];
  _count?: { terms: number };
  createdAt: string;
  updatedAt: string;
}

export interface TermTemplate {
  id: string;
  groupId: string;
  code: string;
  title: string;
  content: string;
  summary?: string;
  version: number;
  sortOrder: number;
  isRequired: boolean;
  effectiveFrom?: string;
  effectiveUntil?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTermGroupDto {
  name: string;
  description?: string;
  shipmentTypes?: ShipmentType[];
  regions?: string[];
}

export interface CreateTermDto {
  code: string;
  title: string;
  content: string;
  summary?: string;
  sortOrder?: number;
  isRequired?: boolean;
  effectiveFrom?: string;
  effectiveUntil?: string;
}

export interface QueryTermGroupParams {
  search?: string;
  shipmentType?: ShipmentType;
  isActive?: boolean;
}

export const termsApi = {
  async getAllGroups(params?: QueryTermGroupParams) {
    const response = await api.get<{
      data: TermGroup[];
      meta: { total: number };
    }>('/tms/terms/groups', { params });
    return response.data;
  },

  async getGroupById(id: string) {
    const response = await api.get<TermGroup>(`/tms/terms/groups/${id}`);
    return response.data;
  },

  async createGroup(data: CreateTermGroupDto) {
    const response = await api.post<TermGroup>('/tms/terms/groups', data);
    return response.data;
  },

  async updateGroup(id: string, data: Partial<CreateTermGroupDto & { isActive?: boolean }>) {
    const response = await api.patch<TermGroup>(`/tms/terms/groups/${id}`, data);
    return response.data;
  },

  async deleteGroup(id: string) {
    const response = await api.delete<void>(`/tms/terms/groups/${id}`);
    return response.data;
  },

  async setDefaultGroup(id: string) {
    const response = await api.post<TermGroup>(`/tms/terms/groups/${id}/set-default`);
    return response.data;
  },

  async createTerm(groupId: string, data: CreateTermDto) {
    const response = await api.post<TermTemplate>(`/tms/terms/groups/${groupId}/terms`, data);
    return response.data;
  },

  async updateTerm(termId: string, data: Partial<CreateTermDto>) {
    const response = await api.patch<TermTemplate>(`/tms/terms/terms/${termId}`, data);
    return response.data;
  },

  async deleteTerm(termId: string) {
    const response = await api.delete<void>(`/tms/terms/terms/${termId}`);
    return response.data;
  },

  async createTermVersion(termId: string, data: { content: string; effectiveFrom?: string }) {
    const response = await api.post<TermTemplate>(`/tms/terms/terms/${termId}/version`, data);
    return response.data;
  },

  async getTermHistory(termId: string) {
    const response = await api.get<TermTemplate[]>(`/tms/terms/terms/${termId}/history`);
    return response.data;
  },
};

// ==================== RATE TEMPLATES ====================

export interface RateTemplateCharge {
  chargeCode: string;
  chargeName: string;
  category: ChargeCategoryExtended;
  calculationType: 'Fixed' | 'Percentage' | 'Variable';
  isRequired: boolean;
  sortOrder: number;
}

export interface RateTemplateSlab {
  minWeight: number;
  maxWeight?: number;
  ratePerUnit?: number;
  unit: 'KG' | 'LB';
  minimumCharge?: number;
}

export interface RateTemplate {
  id: string;
  name: string;
  description?: string;
  shipmentType: ShipmentType;
  rateType?: RateType;
  isDefault: boolean;
  isActive: boolean;
  chargeStructure: RateTemplateCharge[];
  slabStructure?: RateTemplateSlab[];
  companyId: string;
  createdById?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRateTemplateDto {
  name: string;
  description?: string;
  shipmentType: ShipmentType;
  rateType?: RateType;
  chargeStructure: Omit<RateTemplateCharge, 'sortOrder'>[];
  slabStructure?: RateTemplateSlab[];
}

export interface QueryRateTemplateParams {
  shipmentType?: ShipmentType;
  isActive?: boolean;
  search?: string;
}

export const rateTemplatesApi = {
  async getAll(params?: QueryRateTemplateParams) {
    const response = await api.get<{
      data: RateTemplate[];
      meta: { total: number };
    }>('/tms/rate-templates', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<RateTemplate>(`/tms/rate-templates/${id}`);
    return response.data;
  },

  async create(data: CreateRateTemplateDto) {
    const response = await api.post<RateTemplate>('/tms/rate-templates', data);
    return response.data;
  },

  async update(id: string, data: Partial<CreateRateTemplateDto & { isActive?: boolean }>) {
    const response = await api.patch<RateTemplate>(`/tms/rate-templates/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete<void>(`/tms/rate-templates/${id}`);
    return response.data;
  },

  async duplicate(id: string) {
    const response = await api.post<RateTemplate>(`/tms/rate-templates/${id}/duplicate`);
    return response.data;
  },

  async setAsDefault(id: string) {
    const response = await api.post<RateTemplate>(`/tms/rate-templates/${id}/set-default`);
    return response.data;
  },
};

// ==================== RATE ANALYTICS ====================

export interface RateAnalyticsSummary {
  totalRates: number;
  activeRates: number;
  expiringRates: number;
  contractRates: number;
  avgRate: number;
  ratesTrend: number;
  byMode: { mode: TransportMode; count: number; avgRate: number }[];
  byCategory: { category: string; count: number }[];
}

export interface RatesByCarrier {
  carrierId: string;
  carrierName: string;
  carrierCode: string;
  totalRates: number;
  activeRates: number;
  avgRate: number;
  minRate: number;
  maxRate: number;
}

export interface RatesByLane {
  originPort: string;
  destinationPort: string;
  mode: TransportMode;
  rateCount: number;
  avgRate: number;
  minRate: number;
  maxRate: number;
  carriers: number;
}

export interface ExpiringRate {
  id: string;
  sku: string;
  carrier: { id: string; name: string; code: string };
  originPort: string;
  destinationPort: string;
  baseRate: number;
  currency: string;
  validTo: string;
  daysUntilExpiry: number;
}

export interface RateActivityPoint {
  date: string;
  created: number;
  expired: number;
  updated: number;
}

export interface TopCarrier {
  carrierId: string;
  carrierName: string;
  carrierCode: string;
  rateCount: number;
  avgRate: number;
  onTimeDelivery?: number;
}

export interface RateTrendData {
  date: string;
  avgRate: number;
  minRate: number;
  maxRate: number;
  count: number;
}

export const rateAnalyticsApi = {
  async getSummary() {
    const response = await api.get<RateAnalyticsSummary>('/tms/rates/analytics/summary');
    return response.data;
  },

  async getByCarrier(params?: { mode?: TransportMode; limit?: number }) {
    const response = await api.get<RatesByCarrier[]>('/tms/rates/analytics/by-carrier', { params });
    return response.data;
  },

  async getByLane(params?: { mode?: TransportMode; limit?: number }) {
    const response = await api.get<RatesByLane[]>('/tms/rates/analytics/by-lane', { params });
    return response.data;
  },

  async getExpiring(daysAhead: 7 | 14 | 30 | 60 = 30) {
    const response = await api.get<ExpiringRate[]>('/tms/rates/analytics/expiring', {
      params: { daysAhead },
    });
    return response.data;
  },

  async getActivity(days: number = 30) {
    const response = await api.get<RateActivityPoint[]>('/tms/rates/analytics/activity', {
      params: { days },
    });
    return response.data;
  },

  async getTopCarriers(limit: number = 10) {
    const response = await api.get<TopCarrier[]>('/tms/rates/analytics/top-carriers', {
      params: { limit },
    });
    return response.data;
  },

  async getTrends(params?: { laneId?: string; months?: number }) {
    const response = await api.get<RateTrendData[]>('/tms/rates/analytics/trends', { params });
    return response.data;
  },
};

// ==================== RATE BULK IMPORT ====================

export type ImportTemplateFormat = 'Shipwaves' | 'Default' | 'Custom';
export type ImportStatus =
  'Pending' | 'Uploading' | 'Processing' | 'Completed' | 'Failed' | 'PartialSuccess' | 'Cancelled';

export interface ImportError {
  row: number;
  column?: string;
  field?: string;
  message: string;
}

export interface RateBulkImport {
  id: string;
  fileName: string;
  templateFormat: ImportTemplateFormat;
  mode?: TransportMode;
  status: ImportStatus;
  totalRows: number;
  processedRows: number;
  successRows: number;
  failedRows: number;
  errors: ImportError[];
  companyId: string;
  createdById: string;
  createdBy?: { id: string; fullName: string };
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBulkImportDto {
  templateFormat: ImportTemplateFormat;
  mode?: TransportMode;
}

export interface QueryBulkImportParams {
  status?: ImportStatus;
  page?: number;
  limit?: number;
}

export const rateBulkImportApi = {
  async create(data: CreateBulkImportDto) {
    const response = await api.post<RateBulkImport>('/tms/rates/bulk-import', data);
    return response.data;
  },

  async uploadFile(importId: string, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<RateBulkImport>(
      `/tms/rates/bulk-import/${importId}/upload`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  async processImport(importId: string) {
    const response = await api.post<RateBulkImport>(`/tms/rates/bulk-import/${importId}/process`);
    return response.data;
  },

  async cancel(importId: string) {
    const response = await api.post<RateBulkImport>(`/tms/rates/bulk-import/${importId}/cancel`);
    return response.data;
  },

  async getStatus(importId: string) {
    const response = await api.get<RateBulkImport>(`/tms/rates/bulk-import/${importId}`);
    return response.data;
  },

  async getHistory(params?: QueryBulkImportParams) {
    const response = await api.get<{
      data: RateBulkImport[];
      meta: { total: number; page: number; limit: number };
    }>('/tms/rates/bulk-import/history', { params });
    return response.data;
  },

  async downloadTemplate(format: ImportTemplateFormat, mode?: TransportMode) {
    const response = await api.get<Blob>('/tms/rates/bulk-import/template', {
      params: { format, mode },
      responseType: 'blob',
    });
    return response.data;
  },

  async exportRates(filters?: RateFilterDto) {
    const response = await api.get<Blob>('/tms/rates/export', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  },
};
